import {useMemo} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {LinearGradient} from 'expo-linear-gradient'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {
  flashbookCardFacts,
  flashbookCreatorOf,
  flashbookRefOf,
  isFlashbookPost,
} from '#/lib/flashbookPostCard'
import {type NavigationProp} from '#/lib/routes/types'
import {
  type FlashbookRecordView,
  useFlashbookByPostQuery,
  useFlashbookByUriQuery,
} from '#/state/queries/flashbook'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {inkOn} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookFilmPoster} from '#/components/flashbooks/useFlashBookFilmPoster'
import {useFlashBookPlace} from '#/components/flashbooks/useFlashBookPlace'
import {Group3_Stroke2_Corner0_Rounded as Group} from '#/components/icons/Group'
import {PageText_Stroke2_Corner0_Rounded as PageText} from '#/components/icons/PageText'
import {Pencil_Stroke2_Corner0_Rounded as Pencil} from '#/components/icons/Pencil'
import {Play_Filled_Corner0_Rounded as Play} from '#/components/icons/Play'
import {SpeakerVolumeFull_Stroke2_Corner0_Rounded as Speaker} from '#/components/icons/Speaker'
import {VideoClip_Stroke2_Corner0_Rounded as VideoClip} from '#/components/icons/VideoClip'
import {Text} from '#/components/Typography'
import {
  getFlashbookTheme,
  listFlashbookThemeItems,
} from '#/themes-flashbook/registry'

/**
 * A published FlashBook, as a post.
 *
 * A book is two records — the post, and the `app.bsky.feed.flashbook` record it
 * points at — and the feed carries only the first of them. So without this
 * component a published book appears in a timeline as a bare caption with
 * nothing behind it: the post says a book exists and there is no way to see it,
 * let alone open it. What the card has to do, in order:
 *
 *  1. **Say which book it is.** The theme it was written in, how long it is and
 *     how much of it is work — all three ride on the post record itself (see the
 *     publisher), so they are on screen on the first paint rather than after a
 *     request. A feed row that is blank until the network answers reads as a
 *     broken post.
 *  2. **Show the cover**, which does need the record, and paint a page in the
 *     book's own theme until it arrives — so the loading state is the book's
 *     colours rather than a grey box.
 *  3. **Open it**, by the book's own AT-URI rather than by the post, because
 *     that is what the reader reads.
 *  4. **Say when the reader is already inside it**, and open it at the page rather
 *     than at the cover. A feed is not only where new books are found: it is where a
 *     learner meets one they are halfway through and had forgotten about, and a card
 *     that offers the same button for both sends them through the front door of a
 *     book they are in the middle of. The place is the *device's* own fact — see
 *     `#/lib/flashbookShelf` — so this costs no request and cannot be stale.
 *  5. **Hand the book's author their class**, without making them read it first.
 *     A teacher checking on their class has nothing to read a book for, and a report
 *     that is only reachable from the end of a learner's read is a report a teacher
 *     would have to sit the book to see — their own book. So the card carries a
 *     second, quieter door on it, shown only to the account that holds the book's
 *     record, which opens the class report directly; see `isAuthor` below.
 *
 * It is a *book* card, not a link preview: the cover is laid out like a cover,
 * with the title on it, because that is how a reader recognises a book they have
 * already seen — and because the same card is what a message or a share shows,
 * where the post's caption is not there to explain it.
 *
 * It has **no frame of its own**. The card used to draw one — a hairline border and
 * a panel a shade off the page — and a frame inside a feed row is a frame inside a
 * frame: the row already opened with an avatar column, so the card was two nested
 * insets narrower than the post it belonged to. A post's body now begins at the
 * row's own gutter (see the note in `PostFeedItem`), and the cover is the card's own
 * edge — the same judgment the module's card was rebuilt on.
 *
 * The card is **vertical and mobile first**: a portrait cover, and a card that
 * stops growing at a phone's width however wide the column it is in.
 *
 * It used to be a 16:9 banner, which is the shape a *video* is and not the shape a
 * book is, and on a wide screen it became a thousand-pixel letterbox that pushed
 * the way into the book off the bottom of the row — the reader's attention went to
 * the widest thing on the page instead of to the book. Portrait also matches what a
 * cover *is*: artwork, an image and a title, which a creator makes for a phone.
 */
export function FlashBookFeedEmbed({
  post,
  flashbook,
}: {
  post: {uri: string; author: {did: string}; record: unknown}
  /** The book view, when the feed's own response carries one. */
  flashbook?: Record<string, unknown>
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  /*
   * Who is signed in, asked of the agent's own session.
   *
   * The studio decides the creator the same way (`useAgent().session?.did`), and the
   * two answers are one account — the session state the reader reads is built from
   * this agent. Asked here rather than through `useSession` because this is the source
   * a *fixture* can stand up: a browser with no account renders this card, and a card
   * whose author could not be set could not be measured for the person it matters to.
   */
  const agent = useAgent()
  const viewerDid = agent.session?.did ?? null
  const navigation = useNavigation<NavigationProp>()

  const postish = useMemo(
    () => ({record: post.record, flashbook}),
    [post.record, flashbook],
  )
  const ref = useMemo(() => flashbookRefOf(postish), [postish])

  /*
   * Two ways to reach the record, and only one of them is ever taken: the post's
   * back-ref when the publisher managed to write it, and the author's own books
   * when it did not. Both are cached and both are keyed by something stable, so
   * a feed scrolls past a hundred of these without re-asking for a book twice.
   */
  const {data: byUri} = useFlashbookByUriQuery(ref?.uri)
  const {data: byPost} = useFlashbookByPostQuery(
    ref ? '' : post.uri,
    ref ? '' : post.author.did,
  )
  const book: FlashbookRecordView | null = byUri ?? byPost ?? null

  const facts = useMemo(
    () => flashbookCardFacts({post: postish, book}),
    [book, postish],
  )

  /*
   * Whether this learner is already inside this book.
   *
   * Asked of one book rather than of the shelf, and by the *record*: a read is keyed
   * by the book, so the same book found from a profile, a message or a shelf is one
   * read. Both ways of naming it are tried, because neither is guaranteed — the
   * post's own back-ref, which is on the first paint, and the record a lookup found,
   * which is always the book the card is drawing.
   */
  const place = useFlashBookPlace(ref?.uri ?? facts.bookUri)

  const themeItem = useMemo(
    () => listFlashbookThemeItems().find(item => item.id === facts.themeId),
    [facts.themeId],
  )
  const tokens = useMemo(
    () => getFlashbookTheme(facts.themeId).tokens,
    [facts.themeId],
  )
  /*
   * Ink *on* the accent, decided by contrast rather than assumed: the nine
   * themes run from near-black (academy) to pale cyan (magazine), so a hard-coded
   * white label would vanish on half of them. This is the reader's own helper for
   * exactly this question, so the card and the book agree on what is legible.
   */
  const onAccent = useMemo(() => inkOn(tokens.accent), [tokens.accent])

  /*
   * The cover's picture, or nothing.
   *
   * Three sources, in the order the card can get them: the post's own field (no
   * request), the record's artwork or its derived still, and — for a book published
   * before the record carried one — the cover film's opening frame, read here. The
   * last only runs when there is genuinely nothing else to draw, so a healthy row
   * never touches a decoder.
   */
  const filmStill = useFlashBookFilmPoster(
    facts.coverImageUri ? undefined : (facts.coverFilmUri ?? undefined),
    facts.coverStartMs,
  )
  const cover = facts.coverImageUri ?? filmStill ?? null

  const level = gradeLabel(facts.level)
  const subject = subjectLabel(facts.subject)

  /*
   * Whether the person looking at this row is the book's author.
   *
   * Asked of the *book*: the record lives in a repo, and the account holding it is
   * the person who set the book — the same rule the reader gates its class report
   * on, so the card cannot offer a door the book would then refuse to open. It is
   * the DID from the book's own URI rather than the post's author because those are
   * two answers to one question and only one of them is about the book.
   */
  const isAuthor = useMemo(
    () => !!viewerDid && flashbookCreatorOf(facts.bookUri) === viewerDid,
    [facts.bookUri, viewerDid],
  )

  /**
   * The teacher's door, and it opens *past* the book.
   *
   * A teacher checking on their class has no reason to sit the book first, and
   * requiring them to made the report a reward for something they had already done.
   * So this asks the reader for the class report, not for the book: nothing has been
   * read, no place is involved, and the reader's own guard decides whether to honour
   * it — the route is public, so `classReport` can only ever be a request.
   */
  const onOpenClassReport = useMemo(() => {
    // Held in a local so the null check survives into the closure: the reader is
    // addressed by the book's own URI, and a card that has not found the record yet
    // has no book to send it one about.
    const bookUri = facts.bookUri
    if (!isAuthor || !bookUri) return undefined
    return () =>
      navigation.navigate('FlashBookPlay', {
        flashbookid: bookUri,
        classReport: true,
      })
  }, [facts.bookUri, isAuthor, navigation])

  const onOpen = useMemo(() => {
    if (!facts.bookUri) return undefined
    /*
     * `resume` asks the reader to open *at* the place this card just promised. It
     * carries the *fact* that there is one rather than the page itself, because the
     * page is the device's and the reader is the one that holds it: a page number
     * sent through a route could only be a second copy of the truth, free to drift
     * from storage between the tap and the mount.
     */
    const params = place
      ? {flashbookid: facts.bookUri, resume: true}
      : {flashbookid: facts.bookUri}
    return () => navigation.navigate('FlashBookPlay', params)
  }, [facts.bookUri, navigation, place])

  const themeName = themeItem?.name ?? facts.themeId
  const themeEmoji = themeItem?.emoji

  return (
    /*
     * The card is a frame with *two* controls in it, not one control with another
     * inside it.
     *
     * That structure is deliberate, and it is an accessibility decision rather than a
     * layout one: a pressable drawn inside a pressable is one button to iOS's
     * VoiceOver, so the teacher's row would be a row they could see and not reach. So
     * the way into the book is its own pressable, and the teacher's door sits beside
     * it in the card's frame.
     */
    <View
      testID="flashbookFeedEmbed"
      style={[styles.card, a.rounded_md, a.overflow_hidden]}>
      <Pressable
        testID="flashbookFeedEmbedOpen"
        accessibilityRole="button"
        accessibilityLabel={
          place
            ? l`Continue the FlashBook ${facts.title}`
            : l`Read the FlashBook ${facts.title}`
        }
        accessibilityHint={
          place
            ? l`Opens at page ${place.page} of ${place.pageCount}`
            : l`Opens the interactive book`
        }
        disabled={!onOpen}
        onPress={onOpen}
        style={({pressed}) => [styles.open, pressed && styles.pressed]}>
        {/* The cover: the book's own art, a film cover's own frame, or a page in the
          book's own palette while a record is still on its way. */}
        <View testID="flashbookFeedEmbedCover" style={styles.cover}>
          {cover ? (
            <Image
              testID="flashbookFeedEmbedCoverArt"
              source={{uri: cover}}
              style={[a.absolute, a.inset_0]}
              contentFit="cover"
              transition={150}
              accessibilityIgnoresInvertColors
            />
          ) : (
            <LinearGradient
              colors={[tokens.accent, tokens.bgSecondary ?? tokens.bg]}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 1}}
              style={[a.absolute, a.inset_0]}
            />
          )}

          {/* A book's title belongs on its cover, and the scrim is what keeps it
            readable over whatever the creator put behind it. */}
          <LinearGradient
            colors={[
              'rgba(0,0,0,0.32)',
              'rgba(0,0,0,0.08)',
              'rgba(0,0,0,0.72)',
            ]}
            locations={[0, 0.45, 1]}
            style={[a.absolute, a.inset_0]}
            pointerEvents="none"
          />

          <View style={styles.coverTop}>
            <View style={[styles.chip, {backgroundColor: tokens.accent}]}>
              <Text
                style={[styles.chipText, {color: onAccent}]}
                numberOfLines={1}>
                {themeEmoji ? `${themeEmoji} ` : ''}
                {themeName}
              </Text>
            </View>
            {facts.hasCoverFilm && (
              <View style={[styles.chip, styles.chipQuiet]}>
                <VideoClip size="xs" fill="white" />
                <Text style={styles.chipText}>
                  <Trans>Video</Trans>
                </Text>
              </View>
            )}
            {!facts.hasCoverFilm && facts.hasIntroAudio && (
              <View style={[styles.chip, styles.chipQuiet]}>
                <Speaker size="xs" fill="white" />
                <Text style={styles.chipText}>
                  <Trans>Narrated</Trans>
                </Text>
              </View>
            )}
          </View>

          <View style={styles.coverBottom}>
            <Text
              testID="flashbookFeedEmbedTitle"
              style={styles.coverTitle}
              numberOfLines={2}>
              {facts.title}
            </Text>
            {facts.pageCount != null && (
              <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                <PageText size="xs" fill="rgba(255,255,255,0.85)" />
                <Text style={styles.coverMeta}>
                  <Plural
                    value={facts.pageCount}
                    one="# page"
                    other="# pages"
                  />
                  {facts.activityCount != null && facts.activityCount > 0 ? (
                    <>
                      {' · '}
                      <Plural
                        value={facts.activityCount}
                        one="# activity"
                        other="# activities"
                      />
                    </>
                  ) : null}
                </Text>
              </View>
            )}
          </View>

          {/*
          How far in, drawn on the cover itself.

          The same rail the shelf's cards carry, for the same reason: a book a
          learner is part-way through says so on its face, so the card can be
          recognised as a book *they are in* before any of its words are read. It
          takes the book's own progress fill, so it is a colour the theme already
          uses for exactly this.
        */}
          {place && place.pageCount > 0 && (
            <View testID="flashbookFeedEmbedRail" style={styles.rail}>
              <View
                style={[
                  styles.railFill,
                  {
                    backgroundColor: tokens.progressFill,
                    width: `${Math.round(place.pct * 100)}%`,
                  },
                ]}
              />
            </View>
          )}
        </View>

        {/* Below the cover: what the book is for, and the way in. */}
        <View style={[a.px_md, a.py_sm, a.gap_sm]}>
          <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
            {facts.activityCount != null && (
              <View
                style={[
                  styles.chip,
                  styles.chipOnSurface,
                  t.atoms.bg_contrast_50,
                ]}>
                <Pencil size="xs" fill={t.palette.contrast_1000} />
                <Text style={[styles.chipText, t.atoms.text]} numberOfLines={1}>
                  {facts.activityCount > 0 ? (
                    <Plural
                      value={facts.activityCount}
                      one="# interactive exercise"
                      other="# interactive exercises"
                    />
                  ) : (
                    <Trans>Reading book</Trans>
                  )}
                </Text>
              </View>
            )}
            {subject && (
              <View
                style={[
                  styles.chip,
                  styles.chipOnSurface,
                  t.atoms.bg_contrast_50,
                ]}>
                <Text style={[styles.chipText, t.atoms.text]} numberOfLines={1}>
                  {l(subject)}
                </Text>
              </View>
            )}
            {level && (
              <View
                style={[
                  styles.chip,
                  styles.chipOnSurface,
                  t.atoms.bg_contrast_50,
                ]}>
                <Text style={[styles.chipText, t.atoms.text]} numberOfLines={1}>
                  {l(level)}
                </Text>
              </View>
            )}
          </View>

          {/*
          Where the learner is in it, in the same words a shelf card uses.

          Above the button rather than on the cover: how far in is a *fact about
          this reading*, and the cover is the book's own picture. It is what makes
          the button below unambiguous — "Continue" on its own could be anywhere.
        */}
          {place && (
            <Text
              testID="flashbookFeedEmbedPlace"
              numberOfLines={1}
              style={[styles.place, {color: tokens.accent}]}>
              {place.pageCount > 0
                ? l`Page ${place.page} of ${place.pageCount}`
                : l`Page ${place.page}`}
            </Text>
          )}

          <View
            testID="flashbookFeedEmbedCta"
            style={[styles.cta, {backgroundColor: tokens.accent}]}>
            <Play size="xs" fill={onAccent} />
            <Text style={[styles.ctaText, {color: onAccent}]}>
              {place ? <Trans>Continue</Trans> : <Trans>Read the book</Trans>}
            </Text>
          </View>
        </View>
      </Pressable>

      {/*
        The teacher's own row, under their book's way in.

        Quiet, and under the reading action rather than beside it: the card is a book,
        and a teacher who wants to read their own book should still be able to. It
        says *Class report* rather than "see how the class did", because by the time
        they are looking at a card in a feed they have already decided they want the
        numbers.

        Its own control in the card's frame — the row that opens the book ends above
        it — so choosing the class report cannot open the book as well, and cannot be
        mistaken for part of the way in.
      */}
      {onOpenClassReport && (
        <View style={[a.px_md, styles.classRow]}>
          <Pressable
            testID="flashbookFeedEmbedClassReport"
            accessibilityRole="button"
            accessibilityLabel={l`Class report for ${facts.title}`}
            accessibilityHint={l`Opens the class's results without reading the book`}
            onPress={onOpenClassReport}
            style={({pressed}) => [
              styles.classAction,
              {borderColor: tokens.accent},
              pressed && styles.pressed,
            ]}>
            <Group size="xs" fill={tokens.accent} />
            <Text style={[styles.classActionText, {color: tokens.accent}]}>
              <Trans>Class report</Trans>
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  )
}

/**
 * Whether a post should be drawn as a book.
 *
 * Exported so the four places that render a post — the feed row, the thread's
 * anchor and its replies, and a direct message — ask the question in exactly one
 * way. A post is a book if it says so, which is the same rule the card itself
 * uses (`isFlashbookPost`): its own marker, or the back-ref the publisher patches
 * on. The reverse lookup in the card covers the third case, where neither
 * survived, so a false answer here costs nothing but a book that is only a
 * caption.
 */
export function hasFlashbookEmbed(post: {
  record?: unknown
  flashbook?: unknown
}): boolean {
  return isFlashbookPost(post)
}

/**
 * The shape of a book's cover on a card: portrait, like the thing it is a picture
 * of.
 *
 * 4:5 rather than a phone's full 9:16, because a feed row shares the screen with the
 * posts above and below it: a cover as tall as the screen is a card a reader scrolls
 * past instead of into. 4:5 is the portrait shape a timeline has already taught
 * people to expect.
 */
const COVER_ASPECT = 4 / 5

/**
 * The widest a book card is drawn, however wide the column it sits in.
 *
 * A phone gets the full width of its column, which is the width this card is
 * designed at. A desktop column is up to six hundred — and a portrait cover that
 * wide is a 600×750 poster, taller than the fold of a laptop window with the way into
 * the book below it: the card becomes the largest thing on the page and the reader's
 * eye goes to its size rather than to the book. So above a phone's width the card
 * stops growing, the way a photograph is framed rather than stretched. A ceiling
 * rather than a fixed width, so a genuinely narrow column — a message's split view,
 * a small window — still gets a card that fills it.
 */
const CARD_MAX_WIDTH = 420

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: CARD_MAX_WIDTH,
    // Left-aligned in the reading direction rather than centred: the card belongs
    // to the post above it, and a centred block reads as an advert.
    alignSelf: 'flex-start',
  },
  /** The way in: everything above the teacher's row, and all of it pressable. */
  open: {
    cursor: 'pointer',
  },
  /**
   * The teacher's row, inside the card's frame.
   *
   * No top padding: the section above it already ends with its own, and the two
   * together would read as a gap rather than as two halves of one card.
   */
  classRow: {
    paddingBottom: 12,
  },
  pressed: {
    opacity: 0.92,
  },
  cover: {
    width: '100%',
    aspectRatio: COVER_ASPECT,
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: '#111',
  },
  coverTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  coverBottom: {
    gap: 4,
  },
  coverTitle: {
    color: 'white',
    fontSize: 19,
    fontWeight: '700',
    lineHeight: 24,
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 3,
  },
  coverMeta: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 12,
    fontWeight: '600',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    maxWidth: '70%',
  },
  chipQuiet: {
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  chipOnSurface: {
    maxWidth: '100%',
  },
  chipText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  /*
   * On the cover's bottom edge, over the artwork and inside its scrim.
   *
   * The track is a wash of black rather than a theme colour, because what is behind
   * it is the creator's own picture: a track taken from the book's palette would be
   * invisible on the books whose cover art is that colour.
   */
  rail: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 6,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  railFill: {
    height: '100%',
  },
  place: {
    fontSize: 12,
    fontWeight: '700',
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    /*
     * A thumb's height, not a label's: this is the whole point of the card, and
     * a 35px bar is the size a control is when nobody measured one. Padding plus
     * the 20px line of type lands it at 44px, which is the floor both mobile
     * platforms name.
     */
    paddingVertical: 12,
    borderRadius: 10,
  },
  ctaText: {
    fontSize: 15,
    fontWeight: '700',
  },
  /*
   * The teacher's row: an outline in the theme's own accent, on the card's surface.
   *
   * Outlined rather than filled, because it is the second thing on the card and a
   * second filled bar would read as a second way into the same book. The accent
   * rather than a grey, because it is still *this book's* action.
   */
  classAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    // Sized like the way in above it: a teacher on a phone in a corridor is the
    // person this row exists for, and a second door that is harder to hit than the
    // first is the wrong second door.
    paddingVertical: 12,
    borderWidth: 1,
    borderRadius: 10,
  },
  classActionText: {
    fontSize: 14,
    fontWeight: '700',
  },
})
